import React, { useState, useEffect, useCallback } from 'react';
import { getNews, createNews, updateNews, deleteNews, archiveNews, restoreNews } from '../../api';
import '../../styles/AdminStyles.css';

const NewsAdmin = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', excerpt: '', fullText: '' });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNews(100, 0, showArchived);
      setNews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useEffect(() => {
    return () => previews.forEach(url => URL.revokeObjectURL(url));
  }, [previews]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    const updatedFiles = [...files, ...selectedFiles];
    setFiles(updatedFiles);
    const newPreviews = updatedFiles.map(file => URL.createObjectURL(file));
    previews.forEach(url => URL.revokeObjectURL(url));
    setPreviews(newPreviews);
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, idx) => idx !== indexToRemove);
    setFiles(updatedFiles);
    const newPreviews = updatedFiles.map(file => URL.createObjectURL(file));
    previews.forEach(url => URL.revokeObjectURL(url));
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', form.title);
    const dateValue = form.date ? new Date(form.date).toISOString() : new Date().toISOString();
    formData.append('date', dateValue);
    formData.append('excerpt', form.excerpt);
    formData.append('fullText', form.fullText);
    files.forEach(file => formData.append('images', file));

    try {
      if (editing) {
        await updateNews(editing.id, formData);
      } else {
        await createNews(formData);
      }
      resetForm();
      fetchNews();
    } catch (err) {
      console.error(err);
      alert('Ошибка сохранения');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ title: '', date: '', excerpt: '', fullText: '' });
    setFiles([]);
    previews.forEach(url => URL.revokeObjectURL(url));
    setPreviews([]);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      date: item.date ? item.date.split('T')[0] : '',
      excerpt: item.excerpt || '',
      fullText: item.fullText || '',
    });
    setFiles([]);
    setPreviews([]);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить новость безвозвратно? Все изображения также будут удалены.')) {
      try {
        await deleteNews(id);
        fetchNews();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleArchive = async (id, isArchived) => {
    try {
      await archiveNews(id, isArchived);
      fetchNews();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreNews(id);
      fetchNews();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="admin-wrapper">
      <div className="admin-header">
        <h1 className="admin-title">Управление новостями</h1>
        <button onClick={() => window.location.href = '/admin'} className="admin-logout-btn">Назад</button>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Показать архивные новости
          </label>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">{editing ? 'Редактировать новость' : 'Создать новость'}</div>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-row">
            <label>Заголовок *</label>
            <input type="text" name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="admin-form-row">
            <label>Дата *</label>
            <input type="date" name="date" value={form.date} onChange={handleChange} required />
          </div>
          <div className="admin-form-row">
            <label>Краткое описание</label>
            <textarea name="excerpt" value={form.excerpt} onChange={handleChange} rows="3" />
          </div>
          <div className="admin-form-row">
            <label>Полный текст</label>
            <textarea name="fullText" value={form.fullText} onChange={handleChange} rows="6" />
          </div>
          <div className="admin-form-row">
            <label>Изображения (можно несколько)</label>
            <input type="file" multiple onChange={handleFileChange} accept="image/*" />
            {previews.length > 0 && (
              <div className="image-preview-grid">
                {previews.map((url, idx) => (
                  <div key={idx} className="image-preview" style={{ position: 'relative' }}>
                    <img src={url} alt="preview" />
                    <button
                      type="button"
                      className="image-preview-remove"
                      onClick={() => removeFile(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="admin-btn-group">
            <button type="submit" className="admin-btn admin-btn-primary">
              {editing ? 'Обновить' : 'Создать'}
            </button>
            {editing && <button type="button" onClick={resetForm} className="admin-btn admin-btn-secondary">Отмена</button>}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">Список новостей {showArchived && '(включая архив)'}</div>
        {loading && <div className="loading-spinner">Загрузка...</div>}
        {!loading && news.length === 0 && <div>Новостей не найдено</div>}
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr><th>ID</th><th>Заголовок</th><th>Дата</th><th>Статус</th><th>Главное фото</th><th>Действия</th></tr>
            </thead>
            <tbody>
              {news.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.title}</td>
                  <td>{new Date(item.date).toLocaleDateString()}</td>
                  <td>{item.isArchived ? '📦 Архив' : '✅ Активна'}</td>
                  <td>
                   {item.mainImage && <img src={item.mainImage} alt="thumb" style={{ width: '60px', height: '40px', objectFit: 'cover' }} />}
                  </td>
                  <td>
                    <div className="admin-btn-group">
                      <button onClick={() => handleEdit(item)} className="admin-btn admin-btn-secondary">Ред.</button>
                      {item.isArchived ? (
                        <button onClick={() => handleRestore(item.id)} className="admin-btn admin-btn-success">Восст.</button>
                      ) : (
                        <button onClick={() => handleArchive(item.id, true)} className="admin-btn admin-btn-warning">Арх.</button>
                      )}
                      <button onClick={() => handleDelete(item.id)} className="admin-btn admin-btn-danger">Уд.</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsAdmin;